/**
 * Month-string helpers ("YYYY-MM") anchored to America/Barbados.
 *
 * The whole Energy Insights flow uses month strings — not Date objects —
 * to sidestep timezone drift between the browser and the backend. Every
 * "current month" question is answered relative to the app timezone,
 * NOT the device timezone.
 */

import { SESSION_TIMEZONE } from './formatDate';

const MONTH_RE = /^\d{4}-\d{2}$/;

export function isValidMonth(m) {
  return typeof m === 'string' && MONTH_RE.test(m);
}

/**
 * Current local month as "YYYY-MM" in the app timezone.
 */
export function currentMonth(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: SESSION_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(now);
  const year = parts.find((p) => p.type === 'year')?.value;
  const month = parts.find((p) => p.type === 'month')?.value;
  return `${year}-${month}`;
}

/**
 * Add `delta` months (can be negative) to a "YYYY-MM" string.
 * Wraps year boundaries via calendar math so `2026-01` - 1 = `2025-12`.
 */
export function shiftMonth(month, delta) {
  if (!isValidMonth(month)) return month;
  const [y, m] = month.split('-').map(Number);
  const total = y * 12 + (m - 1) + delta;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  return `${ny}-${String(nm).padStart(2, '0')}`;
}

/**
 * Render "July 2026" from "2026-07" in the app timezone. Uses UTC
 * midday of the first-of-month so the process timezone never shifts
 * the label.
 */
export function formatMonthLong(month) {
  if (!isValidMonth(month)) return '';
  const [y, m] = month.split('-').map(Number);
  const anchor = new Date(Date.UTC(y, m - 1, 15, 12, 0, 0));
  try {
    return anchor.toLocaleDateString(undefined, {
      timeZone: SESSION_TIMEZONE,
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return month;
  }
}

/**
 * True when moving to `month` would step past the current local
 * month — used to disable "Next" in the month selector.
 */
export function isFutureMonth(month, now = new Date()) {
  if (!isValidMonth(month)) return false;
  return month > currentMonth(now);
}
